import { Moon, Sun, LogOut, Menu, X } from "lucide-react"
import LOGO from "../assets/logo_h.svg"
import dashboard from "../assets/icons/dashboard.png"
import activity from "../assets/icons/activity.png"
import toast from "react-hot-toast"
import inbox from "../assets/icons/inbox.png"
import applications from "../assets/icons/check.png"
import { useNavigate, useLocation } from "react-router-dom"
import { useEffect, useState } from "react"

const CandidateProfileSidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState("overview")
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const path = location.pathname
    if (path === "/profile" || path === "/") setActiveTab("profile")
    else if (path === "/job-listings") setActiveTab("resume-analysis")
    else if (path === "/dashboard/applications") setActiveTab("applications")
    else if (path === "/inbox") setActiveTab("inbox")
    else if (path === "/dashboard/activity") setActiveTab("activity")
  }, [location.pathname])

  const handleNavigation = (tab, route) => {
    setActiveTab(tab)
    navigate(route)
    setIsMobileMenuOpen(false)
  }

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
    toast.loading(`We're working on it :)`, { icon: "🚧", duration: 3000 })
  }

  const handleSignout = () => {
    console.log("Signing out...")
  }

  const mainMenuItems = [
    {
      id: "profile",
      label: "Profile",
      icon: dashboard,
      route: "/profile",
      active: activeTab === "profile",
    },
    {
      id: "applications",
      label: "Applications",
      icon: applications,
      route: "/dashboard/applications",
      active: activeTab === "applications",
    },
    {
      id: "inbox",
      label: "Inbox",
      icon: inbox,
      route: "/inbox",
      active: activeTab === "inbox",
    },
    {
      id: "activity",
      label: "Activity",
      icon: activity,
      route: "/dashboard/activity",
      active: activeTab === "activity",
    },
  ]

  const NavItem = ({ item }) => (
    <button
      onClick={() => handleNavigation(item.id, item.route)}
      className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-150 ${
        item.active
          ? "bg-[#dfdfdf] text-gray-900"
          : "text-gray-700 hover:bg-white/60 hover:text-gray-900"
      }`}
    >
      <div className="w-5 h-5 mr-3 flex items-center justify-center flex-shrink-0">
        <img
          src={item.icon}
          alt={item.label}
          className="w-4 h-4 object-contain opacity-75"
        />
      </div>
      <span
        className="font-opensans font-normal"
        style={{
          fontSize: "14px",
          fontWeight: 450,
          lineHeight: "20px",
          color: "rgb(40, 40, 40)",
        }}
      >
        {item.label}
      </span>
    </button>
  )

  const SectionLabel = ({ label }) => (
    <p className="px-3 pt-5 pb-3 text-xs font-semibold text-gray-500 tracking-wide font-opensans">
      {label}
    </p>
  )

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-[#f3f3f3]">
      {/* Header */}
      <div className="px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => handleNavigation("profile", "/")}
          className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
        >
          <div className="w-7 h-7 bg-black rounded-md flex items-center justify-center flex-shrink-0">
            <img
              className="w-4 h-4 brightness-0 invert"
              src={LOGO}
              alt="Logo"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-gray-900 font-opensans">
              Resolut
            </span>
            <svg
              className="w-3 h-3 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 9l4-4 4 4M8 15l4 4 4-4"
              />
            </svg>
          </div>
        </button>

        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="md:hidden p-1.5 hover:bg-gray-200 rounded-md transition-colors"
        >
          <X size={18} className="text-gray-600" />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 px-3 overflow-y-auto">
        <SectionLabel label="Menu" />
        <nav className="space-y-0.5">
          {mainMenuItems.map((item) => (
            <NavItem key={item.id} item={item} />
          ))}
        </nav>

        <SectionLabel label="Account" />
        <nav className="space-y-0.5">
          <button
            onClick={handleSignout}
            className="w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-150 text-gray-700 hover:bg-white/60 hover:text-gray-900"
          >
            <div className="w-5 h-5 mr-3 flex items-center justify-center flex-shrink-0">
              <LogOut size={16} className="text-gray-500" />
            </div>
            <span className="text-sm font-opensans text-gray-900">
              Sign Out
            </span>
          </button>

          <button
            onClick={toggleTheme}
            className="w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-150 text-gray-700 hover:bg-white/60 hover:text-gray-900"
          >
            <div className="w-5 h-5 mr-3 flex items-center justify-center flex-shrink-0">
              {isDarkMode ? (
                <Sun size={16} className="text-gray-500" />
              ) : (
                <Moon size={16} className="text-gray-500" />
              )}
            </div>
            <span className="text-sm font-opensans text-gray-900">
              {isDarkMode ? "Light Mode" : "Dark Mode"}
            </span>
          </button>
        </nav>
      </div>

      {/* Bottom toggle icon */}
      <div className="px-3 py-3">
        <button className="p-1.5 rounded-md hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-gray-100 border-b border-gray-200 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          >
            {isMobileMenuOpen ? (
              <X size={20} className="text-gray-700" />
            ) : (
              <Menu size={20} className="text-gray-700" />
            )}
          </button>
          <button
            onClick={() => handleNavigation("profile", "/")}
            className="flex items-center space-x-2"
          >
            <div className="w-7 h-7 bg-black rounded-md flex items-center justify-center">
              <img
                className="w-4 h-4 brightness-0 invert"
                src={LOGO}
                alt="Logo"
              />
            </div>
            <span className="text-sm font-semibold text-gray-900 font-opensans">
              Resolut
            </span>
          </button>
        </div>
      </div>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`h-screen w-60 flex flex-col fixed top-0 left-0 z-50 transition-transform duration-300 ease-in-out md:translate-x-0 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent />
      </div>
    </>
  )
}

export default CandidateProfileSidebar